import React, { useState, useEffect } from 'react';
import baseURL from '../url';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faUtensils, faTimes } from '@fortawesome/free-solid-svg-icons';
import './ClienteMesa.css';

export default function ClienteMesa() {
    const [mesaActiva, setMesaActiva] = useState(null);
    const [modalLlamar, setModalLlamar] = useState(false);
    const [motivo, setMotivo] = useState('Atención general');
    const [enviando, setEnviando] = useState(false);

    useEffect(() => {
        // 1. Revisar si viene ?mesaToken en la URL
        const params = new URLSearchParams(window.location.search);
        const token = params.get('mesaToken');

        if (token) {
            verificarToken(token);
        } else {
            // Verificar si ya está guardada en localStorage
            const guardada = localStorage.getItem('mesaActiva');
            if (guardada) {
                try {
                    const parsed = JSON.parse(guardada);
                    if (parsed && parsed.idMesa) {
                        setMesaActiva(parsed);
                    }
                } catch (e) {
                    console.error(e);
                }
            }
        }

        const handleMesaUpdate = () => {
            const guardada = localStorage.getItem('mesaActiva');
            if (guardada) {
                try {
                    setMesaActiva(JSON.parse(guardada));
                } catch (e) {
                    setMesaActiva(null);
                }
            } else {
                setMesaActiva(null);
            }
        };

        window.addEventListener('mesaActivaUpdated', handleMesaUpdate);
        return () => window.removeEventListener('mesaActivaUpdated', handleMesaUpdate);
    }, []);

    const verificarToken = (token) => {
        fetch(`${baseURL}/mesaTokenGet.php?token=${encodeURIComponent(token)}`)
            .then(res => res.json())
            .then(data => {
                if (data.mesa) {
                    setMesaActiva(data.mesa);
                    localStorage.setItem('mesaActiva', JSON.stringify(data.mesa));
                    window.dispatchEvent(new Event('mesaActivaUpdated'));
                    Swal.fire({
                        title: `¡Bienvenido!`,
                        text: `Te encuentras en ${data.mesa.mesa}. Puedes realizar tus pedidos y llamar al mesero desde aquí.`,
                        icon: 'success',
                        timer: 3500,
                        showConfirmButton: false
                    });
                } else {
                    console.warn('Token de mesa inválido');
                }
            })
            .catch(err => console.error('Error al verificar mesa:', err));
    };

    const salirDeMesa = () => {
        Swal.fire({
            title: '¿Deseas desvincular la mesa actual?',
            text: `Dejarás de estar asociado a ${mesaActiva?.mesa}.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sí, desvincular',
            cancelButtonText: 'Cancelar'
        }).then((result) => {
            if (result.isConfirmed) {
                localStorage.removeItem('mesaActiva');
                setMesaActiva(null);
                window.dispatchEvent(new Event('mesaActivaUpdated'));
                // Limpiar param de la url
                const url = new URL(window.location.href);
                url.searchParams.delete('mesaToken');
                window.history.replaceState({}, '', url.pathname);
            }
        });
    };

    const handleLlamarMesero = async () => {
        if (!mesaActiva || !mesaActiva.idMesa) return;

        setEnviando(true);
        try {
            const res = await fetch(`${baseURL}/llamadaMeseroPost.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    idMesa: mesaActiva.idMesa,
                    motivo: motivo || 'Atención general'
                })
            });

            const data = await res.json();
            setEnviando(false);
            setModalLlamar(false);

            if (res.status === 429) {
                Swal.fire({
                    title: 'Mesero ya notificado',
                    text: data.mensaje || 'Un mesero ya está en camino a tu mesa.',
                    icon: 'info'
                });
            } else if (data.mensaje) {
                Swal.fire({
                    title: '¡Mesero en camino!',
                    text: `Se ha notificado al personal para atender la ${mesaActiva.mesa}.`,
                    icon: 'success',
                    timer: 3000,
                    showConfirmButton: false
                });
            } else {
                Swal.fire({
                    title: 'Atención',
                    text: data.error || 'No se pudo enviar la solicitud.',
                    icon: 'warning'
                });
            }
        } catch (error) {
            setEnviando(false);
            setModalLlamar(false);
            Swal.fire({
                title: 'Error de conexión',
                text: 'Inténtalo de nuevo en unos momentos.',
                icon: 'error'
            });
        }
    };

    if (!mesaActiva) return null;

    return (
        <>
            {/* Banner superior de mesa asignada */}
            <div className="mesa-banner-active">
                <div className="mesa-banner-info">
                    <FontAwesomeIcon icon={faUtensils} className="mesa-banner-icon" />
                    <span>Mesa activa: <strong>{mesaActiva.mesa}</strong></span>
                </div>
                <button className="mesa-banner-close" onClick={salirDeMesa} title="Desvincular mesa">
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>

            {/* Botón flotante para llamar al mesero */}
            <button
                className="btn-llamar-mesero-float"
                onClick={() => setModalLlamar(true)}
                title="Llamar al mesero"
            >
                <FontAwesomeIcon icon={faBell} className="bell-shake" />
                <span className="btn-llamar-text">Llamar al mesero</span>
            </button>

            {/* Modal para solicitar atención */}
            {modalLlamar && (
                <div className="modal modal-llamar-overlay">
                    <div className="modal-content modal-llamar-content">
                        <div className="deFlexBtnsModal">
                            <h3>🔔 Llamar al Mesero</h3>
                            <span className="close" onClick={() => setModalLlamar(false)}>&times;</span>
                        </div>

                        <p className="modal-llamar-sub">
                            ¿En qué podemos ayudarte en la <strong>{mesaActiva.mesa}</strong>?
                        </p>

                        <div className="motivos-grid">
                            {[
                                'Atención general',
                                'Pedir la cuenta',
                                'Limpiar la mesa',
                                'Consulta sobre el menú',
                                'Cubiertos / Servilletas'
                            ].map((m) => (
                                <button
                                    key={m}
                                    type="button"
                                    className={`motivo-btn ${motivo === m ? 'active' : ''}`}
                                    onClick={() => setMotivo(m)}
                                >
                                    {m}
                                </button>
                            ))}
                        </div>

                        <div className="modal-llamar-actions">
                            <button
                                type="button"
                                className="btn-cancelar-llamar"
                                onClick={() => setModalLlamar(false)}
                                disabled={enviando}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="btn-confirmar-llamar"
                                onClick={handleLlamarMesero}
                                disabled={enviando}
                            >
                                {enviando ? 'Enviando llamada...' : 'Confirmar Llamada'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
